import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PRODUCTS } from "@/data/products";
import { PrivatePresentationCTA } from "@/components/PrivatePresentationCTA";
import { useLang, local } from "@/i18n/LanguageContext";

const Collection = () => {
  const { lang, t } = useLang();
  const c = t.collection;

  return (
    <div data-testid="collection-page">
      {/* INTRO --------------------------------------------------------- */}
      <section className="pt-16 md:pt-28 pb-20 md:pb-28 relative overflow-hidden">
        <div className="grain absolute inset-0 pointer-events-none" />
        <div className="relative max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16">
          <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-8">
            {c.eyebrow}
          </p>
          <h1 className="display-hero text-[hsl(var(--md-ink))] max-w-[20ch] leading-[1.02]">
            {c.title}
          </h1>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-10">
            <p className="md:col-span-7 editorial text-[hsl(var(--md-ink))] max-w-[62ch]">
              {c.intro}
            </p>
            <div className="md:col-span-4 md:col-start-9">
              <p className="editorial-muted">
                {c.asideLine1}<br />
                {c.asideLine2}
              </p>
              <p
                data-testid="collection-rendering-note"
                className="font-display italic text-[0.95rem] text-[hsl(28_10%_22%)] leading-relaxed mt-7 max-w-[42ch]"
              >
                {c.renderingNote}
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="hairline max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16" />

      {/* PRODUCTS ------------------------------------------------------ */}
      <section className="pb-24 md:pb-32" data-testid="collection-list">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16">
          {PRODUCTS.map((p, i) => {
            const isEven = i % 2 === 0;
            return (
              <article
                key={p.slug}
                data-testid={`collection-item-${p.slug}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 py-24 md:py-40 border-b border-[hsl(var(--md-line))] last:border-b-0"
              >
                <div
                  className={`md:col-span-7 ${
                    isEven ? "md:order-1" : "md:order-2 md:col-start-6"
                  }`}
                >
                  <Link to={`/collection/${p.slug}`} className="block group relative">
                    <span className="absolute -top-6 md:-top-10 left-0 index-numeral pointer-events-none select-none z-10">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="relative aspect-[5/6] md:aspect-[4/5] bg-[hsl(var(--md-bone))] border border-[hsl(var(--md-line))] overflow-hidden">
                      <img
                        src={p.image}
                        alt={p.name}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.04]"
                        loading="lazy"
                      />
                    </div>
                  </Link>
                </div>

                <div
                  className={`md:col-span-4 flex flex-col justify-center ${
                    isEven ? "md:order-2 md:col-start-9" : "md:order-1 md:col-start-1"
                  }`}
                >
                  <p className="font-eyebrow text-[hsl(var(--md-stone))]">
                    {c.pieceLabel}&nbsp;{String(i + 1).padStart(2, "0")} · {c.pieceCount(p.capacity)}
                  </p>
                  <h2 className="display-1 mt-8 text-[hsl(var(--md-ink))] leading-[1.04]">
                    {p.name}
                  </h2>
                  <p className="font-display italic text-[1.35rem] mt-5 text-[hsl(var(--md-stone))] max-w-[28ch] leading-snug">
                    {local(p.tagline, lang)}
                  </p>
                  <p className="editorial mt-8 max-w-[44ch] text-[hsl(var(--md-ink))]">
                    {local(p.silhouette, lang)}
                  </p>

                  <div className="hairline mt-12" />

                  <div className="mt-8 flex items-baseline justify-between">
                    <span className="font-display text-[1.7rem] text-[hsl(var(--md-ink))]">
                      {local(p.priceLabel, lang)}
                    </span>
                    <Link
                      to={`/collection/${p.slug}`}
                      className="btn-ghost"
                      data-testid={`collection-cta-${p.slug}`}
                    >
                      {c.cta}
                      <ArrowUpRight size={14} strokeWidth={1.25} />
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <PrivatePresentationCTA />
    </div>
  );
};

export default Collection;
